import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Project, Run, Job, Artifact, Event, HealthStatus, LoopStatus } from '@/types'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export const useStore = defineStore('main', () => {
  // State
  const health = ref<HealthStatus | null>(null)
  const projects = ref<Project[]>([])
  const runs = ref<Run[]>([])
  const currentProject = ref<Project | null>(null)
  const currentRun = ref<Run | null>(null)
  const currentJobs = ref<Job[]>([])
  const currentArtifacts = ref<Artifact[]>([])
  const events = ref<Event[]>([])
  const loopStatus = ref<LoopStatus | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const eventSource = ref<EventSource | null>(null)
  const healthPollingInterval = ref<ReturnType<typeof setInterval> | null>(null)

  // Computed
  const activeRuns = computed(() => runs.value.filter(r => r.status === 'running' || r.status === 'queued'))
  const recentEvents = computed(() => events.value.slice(-50).reverse())

  // Actions
  async function fetchHealth() {
    try {
      const res = await fetch(`${API_BASE}/health`)
      health.value = await res.json()
    } catch (e) {
      health.value = { status: 'error', message: String(e) }
    }
  }

  function startHealthPolling() {
    if (healthPollingInterval.value) return
    healthPollingInterval.value = setInterval(fetchHealth, 30000)
  }

  function stopHealthPolling() {
    if (healthPollingInterval.value) {
      clearInterval(healthPollingInterval.value)
      healthPollingInterval.value = null
    }
  }

  async function fetchProjects() {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/projects`)
      const data = await res.json()
      projects.value = data.projects || []
    } catch (e) {
      error.value = `Failed to fetch projects: ${e}`
    } finally {
      loading.value = false
    }
  }

  async function fetchProject(id: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/projects/${id}`)
      const data = await res.json()
      currentProject.value = data.project
      return data.project
    } catch (e) {
      error.value = `Failed to fetch project: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  async function createProject(name: string, repoUrl?: string, repoBranch?: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, repo_url: repoUrl, repo_branch: repoBranch })
      })
      const data = await res.json()
      if (data.project) {
        projects.value.unshift(data.project)
        return data.project
      }
      throw new Error(data.error || 'Failed to create project')
    } catch (e) {
      error.value = `Failed to create project: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchRuns(projectId?: string) {
    loading.value = true
    error.value = null
    try {
      // Fetch runs from all projects or filter client-side
      await fetchProjects()
      
      // For now, we'll fetch runs for each project
      const allRuns: Run[] = []
      for (const project of projects.value) {
        if (projectId && project.id !== projectId) continue
        // Note: API doesn't have a list all runs endpoint, so we track via events
      }
      runs.value = allRuns
    } catch (e) {
      error.value = `Failed to fetch runs: ${e}`
    } finally {
      loading.value = false
    }
  }

  async function fetchRun(id: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/runs/${id}`)
      const data = await res.json()
      currentRun.value = data.run
      return data.run
    } catch (e) {
      error.value = `Failed to fetch run: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  async function createRun(projectId: string, goal: string, mode?: string, jobs?: { kind: string; payload?: Record<string, unknown> }[]) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/projects/${projectId}/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goal, mode, jobs: jobs || [{ kind: 'plan', payload: {} }] })
      })
      const data = await res.json()
      if (data.run) {
        runs.value.unshift(data.run)
        return data
      }
      throw new Error(data.error || 'Failed to create run')
    } catch (e) {
      error.value = `Failed to create run: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  async function cancelRun(id: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/runs/${id}/cancel`, {
        method: 'POST'
      })
      const data = await res.json()
      if (data.run) {
        const idx = runs.value.findIndex(r => r.id === id)
        if (idx >= 0) runs.value[idx] = data.run
        if (currentRun.value?.id === id) currentRun.value = data.run
        return data.run
      }
      throw new Error(data.error || 'Failed to cancel run')
    } catch (e) {
      error.value = `Failed to cancel run: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchJobs(runId: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/runs/${runId}/jobs`)
      const data = await res.json()
      currentJobs.value = data.jobs || []
      return data.jobs
    } catch (e) {
      error.value = `Failed to fetch jobs: ${e}`
      return []
    } finally {
      loading.value = false
    }
  }

  async function fetchArtifacts(runId: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/runs/${runId}/artifacts`)
      const data = await res.json()
      currentArtifacts.value = data.artifacts || []
      return data.artifacts
    } catch (e) {
      error.value = `Failed to fetch artifacts: ${e}`
      return []
    } finally {
      loading.value = false
    }
  }

  async function createDemoJob() {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/demo-job`, {
        method: 'POST'
      })
      return await res.json()
    } catch (e) {
      error.value = `Failed to create demo job: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  function connectEvents(projectId?: string, runId?: string, jobId?: string) {
    if (eventSource.value) {
      eventSource.value.close()
    }

    const params = new URLSearchParams()
    if (projectId) params.set('projectId', projectId)
    if (runId) params.set('runId', runId)
    if (jobId) params.set('jobId', jobId)
    
    const url = `${API_BASE}/events${params.toString() ? '?' + params.toString() : ''}`
    eventSource.value = new EventSource(url)

    eventSource.value.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data) as Event
        events.value.push(event)
        // Keep last 1000 events
        if (events.value.length > 1000) {
          events.value = events.value.slice(-1000)
        }
        
        // Update local state based on events
        handleEvent(event)
      } catch (err) {
        console.error('Failed to parse event:', err)
      }
    }

    eventSource.value.onerror = (e) => {
      console.error('EventSource error:', e)
      // Reconnect after 5 seconds
      setTimeout(() => connectEvents(projectId, runId, jobId), 5000)
    }
  }

  function handleEvent(event: Event) {
    switch (event.type) {
      case 'project.created':
        if (event.projectId && !projects.value.find(p => p.id === event.projectId)) {
          // Refetch projects
          fetchProjects()
        }
        break
      case 'run.created':
      case 'run.status':
        if (event.runId) {
          fetchRun(event.runId)
        }
        break
      case 'job.created':
      case 'job.status':
        if (event.runId && currentRun.value?.id === event.runId) {
          fetchJobs(event.runId)
        }
        break
    }
  }

  function disconnectEvents() {
    if (eventSource.value) {
      eventSource.value.close()
      eventSource.value = null
    }
  }

  async function fetchLoopStatus() {
    try {
      const res = await fetch(`${API_BASE}/loop/status`)
      loopStatus.value = await res.json()
    } catch {
      loopStatus.value = { running: false, mode: null, pid: null, iteration: 0, lastHeartbeat: null, uptime: null }
    }
  }

  async function startLoop(mode: 'production' | 'exploration') {
    loading.value = true
    try {
      const res = await fetch(`${API_BASE}/loop/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      })
      const data = await res.json()
      await fetchLoopStatus()
      return data
    } catch (e) {
      error.value = `Failed to start loop: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  async function stopLoop() {
    loading.value = true
    try {
      const res = await fetch(`${API_BASE}/loop/stop`, {
        method: 'POST'
      })
      const data = await res.json()
      await fetchLoopStatus()
      return data
    } catch (e) {
      error.value = `Failed to stop loop: ${e}`
      return null
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    health,
    projects,
    runs,
    currentProject,
    currentRun,
    currentJobs,
    currentArtifacts,
    events,
    loopStatus,
    loading,
    error,
    // Computed
    activeRuns,
    recentEvents,
    // Actions
    fetchHealth,
    startHealthPolling,
    stopHealthPolling,
    fetchProjects,
    fetchProject,
    createProject,
    fetchRuns,
    fetchRun,
    createRun,
    cancelRun,
    fetchJobs,
    fetchArtifacts,
    createDemoJob,
    connectEvents,
    disconnectEvents,
    fetchLoopStatus,
    startLoop,
    stopLoop
  }
})
